import React, { Component } from 'react';
import moment from 'moment-timezone';
import { every, isEmpty, uniqueId } from 'lodash';
import { Button, Icon } from 'ui';
import LinkButton from 'components/shared/LinkButton';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import { hasFeatureFlags } from 'utils/features';
import { Info } from 'react-feather';
import SessionForm from 'components/InPersonTraining/shared/SessionForm';
import ZoomContext from 'components/InPersonTraining/shared/ZoomContext';
import Error from 'components/InPersonTraining/shared/Error';
import ErrorPage from 'components/shared/ErrorPage';
import TimeZone from 'components/InPersonTraining/shared/SessionForm/TimeZone';
import { appendTimeToObject } from 'components/InPersonTraining/shared/utilities/SessionTime';
import { isValidURL } from 'utils/string_utils';
import Loading from 'components/shared/Loading';
import FeatureFlag from 'components/shared/FeatureFlag';
import RsvpType from './RsvpType';
import GuestInvitations from './GuestInvitation';
import Description from './Description';
import Name from './Name';

class NewEvent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loadingCache: true,
      loading: true,
      pageError: false,
      name: '',
      description: '',
      parentId: '',
      rsvpType: 'single',
      session: {
        id: uniqueId(),
        name: 'Session',
        trainerId: null,
        startTime: moment().add(1, 'hour').startOf('hour'),
        endTime: moment().add(2, 'hour').startOf('hour'),
        timeZone: TimeZone.getLastSelected(),
        capOption: false,
        offSet: '',
        maxAttendees: '',
        location: '',
        videoMeeting: { type: 'none', url: '', id: null, licensed: false },
      },
      sessions: [
        {
          id: uniqueId(),
          name: '',
          trainerId: null,
          startTime: moment().add(1, 'hour').startOf('hour'),
          endTime: moment().add(2, 'hour').startOf('hour'),
          timeZone: TimeZone.getLastSelected(),
          capOption: false,
          offSet: '',
          maxAttendees: '',
          location: '',
          videoMeeting: {
            type: 'none',
            url: '',
            id: null,
            licensed: false,
          },
        },
      ],
      error: null,
      meta: {
        users: [],
        zoomClientId: null,
        currentUser: {},
        currentCompany: {},
        timeZones: [],
      },
      invitationType: 'optional',
      hasEventsMandatory: hasFeatureFlags(['learning_events_mandatory']),
    };
  }

  componentDidMount() {
    const urlParams = new URLSearchParams(window.location.search);
    const formStateKey = urlParams.get('formStateKey');

    request
      .get(routes.IN_PERSON_TRAINING.NEW_EVENT)
      .then(payload => {
        this.setState(prevState => ({
          ...payload,
          sessions: prevState.sessions.map(session =>
            session.name === ''
              ? Object.assign(session, {
                  trainerId: payload.meta.currentUser.id,
                })
              : session,
          ),
          session: {
            ...prevState.session,
            trainerId: payload.meta.currentUser.id,
          },
          name: urlParams.get('event_name') || '',
          description: urlParams.get('event_description') || '',
          parentId: urlParams.get('event_parent_id') || '',
          loading: false,
        }));
      })
      .catch(() => {
        this.setState({ pageError: true, loading: false });
      });

    if (formStateKey) {
      this.setStateFromCacheKey(formStateKey);
    } else {
      this.setState({ loadingCache: false });
    }
  }

  get allSessionsValid() {
    return this.state.sessions.every(session => this.isValid(session));
  }

  isValid(session) {
    return every([
      !!this.state.name.trim(),
      moment(session.startTime).isValid(),
      moment(session.endTime).isValid(),
      !isEmpty(session.timeZone),
      this.isVideoConferencingValid(session),
      this.isZoomTimeValid(session),
    ]);
  }

  isVideoConferencingValid(session) {
    const { type, url } = session.videoMeeting;

    if (type !== 'other') return true;

    return isValidURL(url);
  }

  isZoomTimeValid() {
    const startTime = moment(this.state.session.startTime);
    const isStartBeforeCurrent = startTime.isBefore(moment());
    const { type } = this.state.session.videoMeeting;
    let isValid = true;
    if (type === 'zoom' && isStartBeforeCurrent) {
      isValid = false;
    }

    return isValid;
  }

  handleInputChange = e => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  handleSessionInputChange = (e, sessionId) => {
    const { name, value } = e.target;
    const sessionToUpdate = this.getSessionToUpdate(sessionId);
    sessionToUpdate[name] = value;

    this.setState(prevState => ({
      sessions: prevState.sessions,
    }));
  };

  handleSessionNameChange = (e, sessionId) => {
    const sessionToUpdate = this.getSessionToUpdate(sessionId);
    sessionToUpdate.name = e.target.value;

    this.setState(prevState => ({
      sessions: prevState.sessions,
    }));
  };

  handleEndTimeChange = (endTime, sessionId) => {
    const sessionToUpdate = this.getSessionToUpdate(sessionId);
    sessionToUpdate.endTime = endTime;

    this.setState(prevState => ({
      sessions: prevState.sessions,
    }));
  };

  handleStartTimeChange = (date, sessionId) => {
    const sessionToUpdate = this.getSessionToUpdate(sessionId);
    sessionToUpdate.startTime = date;
    sessionToUpdate.endTime = moment(date).set({
      hour: moment(sessionToUpdate.endTime).get('hour'),
      minute: moment(sessionToUpdate.endTime).get('minute'),
    });

    this.setState(prevState => ({
      sessions: prevState.sessions,
    }));
  };

  handleTimeZoneChange = (timeZone, timeZoneOffSet, sessionId) => {
    const sessionToUpdate = this.getSessionToUpdate(sessionId);
    sessionToUpdate.timeZone = timeZone;

    this.setState(
      prevState => ({
        sessions: prevState.sessions,
      }),
      () => {
        TimeZone.setLastSelected(timeZone);
      },
    );
  };

  handleTrainerChange = (trainerId, sessionId) => {
    const sessionToUpdate = this.getSessionToUpdate(sessionId);
    sessionToUpdate.trainerId = trainerId;

    this.setState(prevState => ({
      sessions: prevState.sessions,
    }));
  };

  resetMaxAttendees(capOption, sessionId) {
    if (capOption) return;
    const sessionToUpdate = this.getSessionToUpdate(sessionId);

    sessionToUpdate.maxAttendees = '';

    this.setState(prevState => ({
      sessions: prevState.sessions,
    }));
  }

  handleCapOptionsChange = (e, sessionId) => {
    const capOption = JSON.parse(e.target.value);
    const sessionToUpdate = this.getSessionToUpdate(sessionId);
    sessionToUpdate.capOption = capOption;

    this.setState(
      prevState => ({
        sessions: prevState.sessions,
      }),
      () => {
        this.resetMaxAttendees(capOption, sessionId);
      },
    );
  };

  handleVideoConferenceChange = data => {
    const sessionToUpdate = this.getSessionToUpdate(data.sessionId);
    delete data.sessionId;
    sessionToUpdate.videoMeeting = data;

    this.setState(prevState => ({
      sessions: prevState.sessions,
    }));
  };

  handleRsvpTypeChange = e => {
    const selectedValue = e.target.value;
    this.setState({ rsvpType: selectedValue });
    if (this.state.sessions.length > 1 && selectedValue === 'single') {
      this.setState({ invitationType: 'optional' });
    }
  };

  getSessionToUpdate = sessionId => {
    const sessionToUpdate = this.state.sessions.find(
      ses => ses.id === sessionId,
    );

    return sessionToUpdate;
  };

  cacheFormState = () => {
    const { state } = this;
    return request
      .postJSON('/current_user/cache', state)
      .then(response => response.key);
  };

  setStateFromCacheKey = formStateKey => {
    return request.get(`/current_user/cache/${formStateKey}`).then(data => {
      if (data) {
        const { session } = data;

        if (!session) {
          this.setState(data);
        } else {
          const newSessionData = {
            ...session,
            startTime: moment.parseZone(session.startTime),
            endTime: moment.parseZone(session.endTime),
          };
          this.setState({
            ...data,
            session: newSessionData,
            loadingCache: false,
          });
        }
      }
    });
  };

  handleAddSession = () => {
    const newSession = [
      {
        id: uniqueId(),
        name: '',
        trainerId: this.state.meta.currentUser.id,
        startTime: moment().add(1, 'hour').startOf('hour'),
        endTime: moment().add(2, 'hour').startOf('hour'),
        timeZone: TimeZone.getLastSelected(),
        capOption: false,
        offSet: '',
        maxAttendees: '',
        location: '',
        videoMeeting: {
          type: 'none',
          url: '',
          id: null,
          licensed: false,
        },
      },
    ];

    this.setState(prevState => ({
      sessions: prevState.sessions.concat(newSession),
    }));

    if (
      this.state.rsvpType === 'single' &&
      this.state.invitationType === 'mandatory'
    ) {
      this.setState({ invitationType: 'optional' });
    }
  };

  handleDeleteSession = id => {
    this.setState(prevState => ({
      sessions: prevState.sessions.filter(ses => ses.id !== id),
    }));
  };

  handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();
    this.state.sessions[0].name = 'Session 1';

    request
      .postJSON(routes.IN_PERSON_TRAINING.EVENTS, {
        event: {
          name: this.state.name,
          description: this.state.description,
          parentId: this.state.parentId,
          isMandatory: this.state.invitationType === 'mandatory',
        },
        session: appendTimeToObject({
          object: this.state.sessions[0],
          startTime: this.state.sessions[0].startTime,
          endTime: this.state.sessions[0].endTime,
        }),
      })
      .then(res => {
        if (res.success) {
          window.location = routes.IN_PERSON_TRAINING.EVENT(res.id);
        }
      })
      .catch(error => {
        const { responseJSON, responseText } = error;
        this.setState({ error: responseJSON || responseText });
      });
  };

  constSessionAttributes = () => {
    const attributes = this.state.sessions.map(session =>
      (({ startTime, endTime, timeZone }) => ({
        startTime,
        endTime,
        timeZone,
      }))(session),
    );

    return attributes;
  };

  handleSubmitMultiSessionEvent = e => {
    e.preventDefault();
    e.stopPropagation();

    const sessionsData = this.state.sessions.map(session => ({
      ...session,
      startTime: moment(session.startTime).tz('utc', true).format(),
      endTime: moment(session.endTime).tz('utc', true).format(),
    }));

    request
      .postJSON(routes.TRAINING.EVENTS, {
        event: {
          name: this.state.name,
          description: this.state.description,
          parentId: this.state.parentId,
          isMandatory: this.state.invitationType === 'mandatory',
          rsvpType: this.state.rsvpType,
          sessions: sessionsData,
        },
      })
      .then(res => {
        window.location = routes.IN_PERSON_TRAINING.EVENT(res.id);
      })
      .catch(error => {
        const { responseJSON, responseText } = error;
        this.setState({ error: responseJSON || responseText });
      });
  };

  handleInvitationTypeChange = e => {
    this.setState({ invitationType: e.target.value });
  };

  render() {
    if (this.state.pageError) {
      return <ErrorPage />;
    }
    const { name, description, session, error, hasEventsMandatory } =
      this.state;
    const { videoMeeting } = session;
    const isZoom = videoMeeting && videoMeeting.type === 'zoom';
    const isMultiSession = this.state.sessions.length > 1;

    const { users, timeZones, currentUser, currentCompany, zoomClientId } =
      this.state.meta;
    return (
      <Loading loading={this.state.loading}>
        <form
          onSubmit={
            isMultiSession
              ? this.handleSubmitMultiSessionEvent
              : this.handleSubmit
          }
          className="NewEventForm ff-ss mt-32"
        >
          <h1 className="ff-ss mb-32">
            {t('in_person_training.new_event.heading')}
          </h1>
          <Text variant={TEXT_VARIANTS.SUBHEADER} className="mb-16">
            {t('in_person_training.new_event.event_information')}
          </Text>
          <Name
            className="NewEventName"
            value={name}
            onChange={this.handleInputChange}
          />
          <Description value={description} onChange={this.handleInputChange} />
          <div className="d-f fd-c">
            <Text variant={TEXT_VARIANTS.SUBHEADER} className="mb-4">
              {t('in_person_training.new_event.session_details')}
            </Text>
            <FeatureFlag features={['readiness-enable-multisession-events']}>
              <Text
                variant={TEXT_VARIANTS.INSTRUX_SMALL_SEMIBOLD}
                className="mb-4"
              >
                {t('in_person_training.new_event.session_limit')}
              </Text>
            </FeatureFlag>
          </div>
          {this.state.sessions.map(singleSession => (
            <ZoomContext.Provider
              value={{ zoomClientId, cacheFormState: this.cacheFormState }}
            >
              <div className="NewEventForm-sessionForm mb-24">
                {!this.state.loadingCache && (
                  <SessionForm
                    key={'sessionForm-' + singleSession.id}
                    id={singleSession.id}
                    session={singleSession}
                    viaAddSessionModal={false}
                    sessions={this.state.sessions}
                    isMultiSession={isMultiSession}
                    users={users}
                    currentUser={currentUser}
                    currentCompany={currentCompany}
                    timeZones={timeZones}
                    canEditTime
                    handleSessionNameChange={this.handleSessionNameChange}
                    handleStartTimeChange={this.handleStartTimeChange}
                    handleEndTimeChange={this.handleEndTimeChange}
                    handleTimeZoneChange={this.handleTimeZoneChange}
                    handleTrainerChange={this.handleTrainerChange}
                    handleCapOptionsChange={this.handleCapOptionsChange}
                    handleSessionInputChange={this.handleSessionInputChange}
                    handleDeleteSession={this.handleDeleteSession}
                    handleVideoConferenceChange={
                      this.handleVideoConferenceChange
                    }
                  />
                )}
              </div>
            </ZoomContext.Provider>
          ))}
          {error && <Error {...error} />}
          <div>
            <FeatureFlag features={['readiness-enable-multisession-events']}>
              <div className="mb-40 mt-24">
                <LinkButton
                  className="mr-8 trk_button_learning_add-session"
                  disabled={this.state.sessions.length > 9}
                  type="primary"
                  onClick={this.handleAddSession}
                >
                  <Icon name="add" className="mr-4" />
                  {t('in_person_training.event_overview.session.add')}
                </LinkButton>
              </div>
            </FeatureFlag>
            {isMultiSession && (
              <>
                <div className="mb-16">
                  <Text variant={TEXT_VARIANTS.SUBHEADER}>
                    {hasEventsMandatory
                      ? t('in_person_training.new_event.rsvp_type_mandatory')
                      : t('in_person_training.new_event.rsvp_type')}
                  </Text>
                </div>
                <RsvpType
                  value={this.state.rsvpType}
                  onChange={this.handleRsvpTypeChange}
                  className
                />
              </>
            )}
            <FeatureFlag features={['learning_events_mandatory']}>
              <div className="mb-16">
                <Text variant={TEXT_VARIANTS.SUBHEADER}>
                  {t('in_person_training.new_event.invitations')}
                </Text>
              </div>
              <GuestInvitations
                value={this.state.invitationType}
                rsvpType={isMultiSession ? this.state.rsvpType : 'all'}
                onChange={this.handleInvitationTypeChange}
              />
              <div className="InvitationFooterInfo">
                <Info
                  className="mr-8 ml-10 mt-20 llui-text-body-light"
                  size={14}
                />
                <p
                  className="llui-text llui-theme-text InvitationFooterText mt-16"
                  dangerouslySetInnerHTML={{
                    __html: isMultiSession
                      ? t(
                          'in_person_training.new_event.invitations_multiple_info',
                        )
                      : t(
                          'in_person_training.new_event.invitations_single_info',
                        ),
                  }}
                />
              </div>
            </FeatureFlag>
            <Button
              customColor
              tall
              submit
              disabled={!this.allSessionsValid}
              ariaLabel={
                isZoom
                  ? t('in_person_training.new_event.create_event_with_zoom')
                  : t('in_person_training.new_event.create_event')
              }
            >
              {isZoom
                ? t('in_person_training.new_event.create_event_with_zoom')
                : t('in_person_training.new_event.create_event')}
            </Button>
          </div>
        </form>
      </Loading>
    );
  }
}

export default NewEvent;
